import clsx from "clsx";
import { ReactNode } from "react";
import { formatCompact, formatExact } from "../../utils/format-number";

/** The fixed categorical order. A series keeps its slot regardless of series count. */
export const SERIES = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)"];

/** Sequential ramp, light→dark, for ordered magnitude buckets. */
export const SEQUENTIAL = [
  "var(--chart-seq-1)",
  "var(--chart-seq-2)",
  "var(--chart-seq-3)",
  "var(--chart-seq-4)",
  "var(--chart-seq-5)",
  "var(--chart-seq-6)",
];

export const GRID = "var(--chart-grid)";
export const AXIS = "var(--chart-axis)";

export const axisTick = {
  fill: AXIS,
  fontSize: 11,
  fontWeight: 500,
} as const;

/**
 * Legend.
 *
 * Always present for two or more series — colour-matching alone is not an identity
 * channel, and it is what discharges the sub-3:1 contrast warning on the lighter
 * slots. Marks mirror the form: a rect for bars and areas, a short rule for lines.
 */
export function Legend({
  items,
  variant = "rect",
  className,
}: {
  items: { label: string; color: string }[];
  variant?: "rect" | "line";
  className?: string;
}) {
  return (
    <ul className={clsx("flex flex-wrap items-center gap-x-4 gap-y-1.5", className)}>
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={clsx("shrink-0", variant === "line" ? "w-3 h-[2px] rounded-full" : "w-2.5 h-2.5 rounded-[3px]")}
            style={{ background: item.color }}
          />
          <span className="text-[11px] font-medium text-light-gray whitespace-nowrap">
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Shared tooltip surface for the recharts plots.
 *
 * Values lead and labels follow — the reader already knows which series they are
 * pointing at and wants the number. Series names arrive from the API, so they go in
 * as text nodes via JSX interpolation, never as raw HTML.
 */
export function ChartTooltip({
  title,
  rows,
  currency,
}: {
  title?: ReactNode;
  rows: { label: string; value: unknown; color?: string }[];
  currency?: boolean;
}) {
  return (
    <div className="rounded-xl border border-edge bg-surface-elevated shadow-overlay px-3 py-2.5 min-w-[160px]">
      {title ? (
        <p className="text-[11px] font-semibold text-light-gray mb-1.5">{title}</p>
      ) : null}
      <div className="flex flex-col gap-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 min-w-0">
              {r.color ? (
                <span
                  aria-hidden
                  className="w-3 h-[2px] rounded-full shrink-0"
                  style={{ background: r.color }}
                />
              ) : null}
              <span className="text-[11px] font-medium text-light-gray truncate">
                {r.label}
              </span>
            </span>
            <span className="text-[12px] font-bold text-text-primary tabular-nums shrink-0">
              {formatExact(r.value, currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Axis tick formatter — compact so ticks never collide. */
export const compactTick = (v: any) => formatCompact(v);

/** Empty state used when a panel has no rows to draw. */
export function ChartEmpty({ message = "No data for this period" }: { message?: string }) {
  return (
    <div className="h-full w-full min-h-[120px] flex items-center justify-center">
      <p className="text-[12px] font-medium text-light-gray">{message}</p>
    </div>
  );
}
